import React, { useEffect, useState } from "react";
import { useNavigate, useParams, useRouteLoaderData } from "react-router-dom";

import { GameLayout } from "@components/Layouts";
import { BackendPromise, url_reverse } from "@components/Utils";
import Accounts from "@screens/Game/Accounts";
import Achievements from "@screens/Game/Achievements";
import Admin from "@screens/Game/Admin";
import ApkUpload from "@screens/Game/ApkUpload";
import Betatesters from "@screens/Game/Betatesters";
import BetatestersUploadList from "@screens/Game/BetatestersUploadList";
import Codes from "@screens/Game/Codes";
import Community from "@screens/Game/Community";
import Distributions from "@screens/Game/Distributions";
import FeaturingLaunch from "@screens/Game/FeaturingLaunch";
import FeaturingNewsletter from "@screens/Game/FeaturingNewsletter";
import FeaturingPromo from "@screens/Game/FeaturingPromo";
import GameBox from "@screens/Game/GameBox";
import GameItems from "@screens/Game/GameItems";
import Highlights from "@screens/Game/Highlights";
import Locales from "@screens/Game/Locales";
import MicroReviews from "@screens/Game/MicroReviews";
import ModeratorComments from "@screens/Game/ModeratorComments";
import Playtest from "@screens/Game/Playtest";
import { Campaigns, Prices } from "@screens/Game/Prices";
import { Publication } from "@screens/Game/Publication";
import Relationship from "@screens/Game/Relationship";
import S3Mirrors from "@screens/Game/S3Mirrors";
import Serials from "@screens/Game/Serials";
import StatisticsGeneral from "@screens/Game/Statistics/General";
import StatisticsMyTracker from "@screens/Game/Statistics/MyTracker";
import StatisticsOnlines from "@screens/Game/Statistics/Onlines";
import StatisticsOrders from "@screens/Game/Statistics/Orders";
import StatisticsPayments from "@screens/Game/Statistics/Payments";
import StatisticsRefunds from "@screens/Game/Statistics/Refunds";
import StatisticsSunduki from "@screens/Game/Statistics/Sunduki";
import StatisticsWishlist from "@screens/Game/Statistics/Wishlist";
import Streams from "@screens/Game/Streams";
import System from "@screens/Game/System";
import Unpublish from "@screens/Game/Unpublish";
import NotFound from "@screens/NotFound";

export default function GameScreen({ screen }) {
    const params = useParams();
    const navigate = useNavigate();
    const gameId = parseInt(params.gameId);
    const [redirectUrl, setRedirectUrl] = useState(null);

    useEffect(() => {
        if (redirectUrl) {
            navigate(redirectUrl, { replace: true });
        }
    }, [redirectUrl]);

    const gameCommonInfo = useRouteLoaderData("gameCommonInfo");
    if (!gameCommonInfo.game) {
        return null;
    }

    const locl = params.locl || gameCommonInfo.game.base_locale;
    const header = <GameHeaderTitle game={gameCommonInfo.game} developer={gameCommonInfo.developer} />;

    let active = "";
    let body = "";
    const props = { gameId, locl, header };
    switch (screen) {
        case "reroute": {
            const redirect_url = reroute(gameCommonInfo);
            if (redirect_url && redirect_url !== redirectUrl) {
                setRedirectUrl(redirect_url);
            }
            break;
        }
        case "admin": {
            active = "admin";
            body = <Admin {...props} />;
            break;
        }
        case "accounts": {
            active = "accounts";
            body = <Accounts {...props} />;
            break;
        }
        case "apk_upload": {
            active = "system";
            body = <ApkUpload {...props} />;
            break;
        }
        case "publication_moderator": {
            active = "publication_moderator";
            body = <Publication {...props} />;
            break;
        }
        case "publication_developer": {
            active = "publication_developer";
            body = <Publication {...props} />;
            break;
        }
        case "locale_edit": {
            active = "locale_edit";
            body = <Locales {...props} />;
            break;
        }
        case "prices": {
            active = "prices";
            body = <Prices {...props} />;
            break;
        }
        case "playtest": {
            active = "playtest";
            body = <Playtest {...props} />;
            break;
        }
        case "s3mirrors": {
            active = "s3mirrors";
            body = <S3Mirrors {...props} />;
            break;
        }
        case "system": {
            active = "system";
            body = <System {...props} />;
            break;
        }
        case "distributions": {
            active = "distributions";
            body = <Distributions {...props} />;
            break;
        }
        case "betatesters": {
            active = "betatesters.list";
            body = <Betatesters {...props} />;
            break;
        }
        case "betatesters_upload_list": {
            active = "betatesters.upload_list";
            body = <BetatestersUploadList {...props} />;
            break;
        }
        case "stream": {
            active = "stream";
            body = <Streams {...props} />;
            break;
        }
        case "campaigns": {
            active = "prices";
            body = <Campaigns {...props} />;
            break;
        }
        case "featuring_launch": {
            active = "featuring.launch";
            body = <FeaturingLaunch {...props} />;
            break;
        }
        case "featuring_promo": {
            active = "featuring.promo";
            body = <FeaturingPromo {...props} />;
            break;
        }
        case "events": {
            active = `events.newsletter.${params.newsletter_id}`;
            body = <FeaturingNewsletter {...props} />;
            break;
        }
        case "achievements": {
            active = "achievements";
            body = <Achievements {...props} />;
            break;
        }
        case "microreviews": {
            active = "microreviews";
            body = <MicroReviews {...props} />;
            break;
        }
        case "relationship": {
            active = "relationship";
            body = <Relationship {...props} />;
            break;
        }
        case "statistics_orders": {
            active = "statistics.orders";
            body = <StatisticsOrders {...props} />;
            break;
        }
        case "statistics_payments": {
            active = "statistics.payments";
            body = <StatisticsPayments {...props} />;
            break;
        }
        case "statistics_refunds": {
            active = "statistics.refunds";
            body = <StatisticsRefunds {...props} />;
            break;
        }
        case "statistics_sunduki": {
            active = "statistics.sunduki";
            body = <StatisticsSunduki {...props} />;
            break;
        }
        case "statistics_wishlist": {
            active = "statistics.wishlist";
            body = <StatisticsWishlist {...props} />;
            break;
        }
        case "statistics_my_tracker": {
            active = "statistics.mytracker";
            body = <StatisticsMyTracker {...props} />;
            break;
        }
        case "statistics_general": {
            active = "statistics.general";
            body = <StatisticsGeneral {...props} />;
            break;
        }
        case "statistics_onlines": {
            active = "statistics.onlines";
            body = <StatisticsOnlines {...props} />;
            break;
        }
        case "community": {
            active = "community";
            body = <Community {...props} />;
            break;
        }
        case "serials": {
            active = "serials";
            body = <Serials {...props} />;
            break;
        }
        case "codes": {
            active = "codes";
            body = <Codes {...props} />;
            break;
        }
        case "unpublish": {
            active = "unpublish";
            body = <Unpublish {...props} />;
            break;
        }
        case "moderator_comments": {
            active = "moderator_comments";
            body = <ModeratorComments {...props} />;
            break;
        }
        case "gameitems": {
            active = "gameitems";
            body = <GameItems {...props} />;
            break;
        }
        case "highlights": {
            active = "highlights";
            body = <Highlights {...props} />;
            break;
        }
        case "highlights_draft": {
            active = "highlights";
            body = <Highlights {...props} isDraftUrl={true} />;
            break;
        }
        case "gamebox": {
            active = "gamebox";
            body = <GameBox {...props} />;
            break;
        }
        default: {
            break;
        }
    }

    if (!body) {
        return <NotFound />;
    }

    return <GameLayout body={body} active={active} />;
}

function GameHeaderTitle({ game, developer }) {
    return game && developer ? (
        <>
            {game.base_locale_object.name} –{" "}
            <a href={url_reverse("devs:accounts", { dev_id: game.developer_id })} target="_blank">
                {developer.name}
            </a>
        </>
    ) : null;
}

function reroute(gameCommonInfo) {
    const first_block = Object.values(gameCommonInfo.game_menu_availability)[0];
    if (!first_block) {
        return;
    }
    const first_menu = Object.values(first_block)[0];
    if (!first_menu) {
        return;
    }
    if ("submenu" in first_menu) {
        return first_menu.submenu[0].url;
    }
    return first_menu.url;
}

export const gameCommonInfoLoader = async ({ params }) => {
    return await BackendPromise.get(url_reverse("api:game:common_info", { game_id: params.gameId }));
};
